import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import MarginHistoryModal from "./marginHistoryPanel";
import { addMarginRatio, updateMarginRatio } from "../../Redux/Actions/propertyActions";

const MarginRatioPopup = ({ isOpen, onClose, propertyId, existingData = null }) => {
  const dispatch = useDispatch();
  const isEdit = !!existingData;

  const [form, setForm] = useState({
    agent_margin: "",
    admin_margin: "",
  });
  const [loading, setLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false); 

  useEffect(() => {
    if (existingData) {
      const toSafe = (val) => {
        if (val === null || val === undefined) return "";
        if (typeof val === "object") return "";
        return String(val);
      };
      setForm({
        agent_margin: toSafe(existingData.agent_margin),
        admin_margin: toSafe(existingData.admin_margin),
      });
    } else {
      setForm({ agent_margin: "", admin_margin: "" });
    }
    setShowHistoryModal(false); // ✅ reset on open
  }, [existingData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const hasAtLeastOne = form.agent_margin !== "" || form.admin_margin !== "";
    if (!hasAtLeastOne) return;

    setLoading(true);
    const payload = { property_id: propertyId };
    if (form.agent_margin !== "") payload.agent_margin = Number(form.agent_margin);
    if (form.admin_margin !== "") payload.admin_margin = Number(form.admin_margin);

    const action = isEdit ? updateMarginRatio : addMarginRatio;
    const res = await dispatch(action(payload));
    setLoading(false);
    if (res?.statusCode === 200) onClose();
  };

  const totalMargin =
    (Number(form.agent_margin) || 0) + (Number(form.admin_margin) || 0);

  return (
    <>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <div style={styles.iconBox}>📊</div>
              <div>
                <h2 style={styles.title}>{isEdit ? "Update" : "Add"} Margin Ratio</h2>
                <p style={styles.subtitle}>Set agent & admin margin percentages</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* ✅ ℹ button opens history modal */}
              <button
                style={{
                  ...styles.closeBtn,
                  background: "rgba(44,71,145,0.08)",
                  border: "1px solid rgba(44,71,145,0.2)",
                  color: "#2c4791",
                  fontSize: "15px",
                  fontWeight: 700,
                }}
                onClick={() => setShowHistoryModal(true)}
                title="View Margin Ratio History"
              >
                ℹ
              </button>
              <button style={styles.closeBtn} onClick={onClose}>✖</button>
            </div>
          </div>

          {/* Fields */}
          <div style={styles.fieldsGrid}>
            {/* Agent Margin */}
            <div style={styles.fieldCard}>
              <div style={styles.fieldHeader}>
                <span style={styles.fieldEmoji}>🧑‍💼</span>
                <div>
                  <p style={styles.fieldTitle}>Agent Margin</p>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 2.5"
                  value={form.agent_margin}
                  onChange={(e) => setForm((f) => ({ ...f, agent_margin: e.target.value }))}
                  style={styles.input}
                />
                <span style={styles.inputSuffix}>%</span>
              </div>
              {form.agent_margin && (
                <div style={styles.fieldPreview}>
                  <span style={{ color: "#60a5fa" }}>Agent gets {form.agent_margin}%</span>
                </div>
              )}
            </div>

            {/* Admin Margin */}
            <div style={styles.fieldCard}>
              <div style={styles.fieldHeader}>
                <span style={styles.fieldEmoji}>🏛️</span>
                <div>
                  <p style={styles.fieldTitle}>Admin Margin</p>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 3.0"
                  value={form.admin_margin}
                  onChange={(e) => setForm((f) => ({ ...f, admin_margin: e.target.value }))}
                  style={styles.input}
                />
                <span style={styles.inputSuffix}>%</span>
              </div>
              {form.admin_margin && (
                <div style={styles.fieldPreview}>
                  <span style={{ color: "#a78bfa" }}>Admin gets {form.admin_margin}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Total Preview */}
          {(form.agent_margin || form.admin_margin) && (
            <div style={styles.totalPreview}>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Agent Margin</span>
                <span style={styles.totalVal}>{form.agent_margin || 0}%</span>
              </div>
              <div style={styles.divider} />
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Admin Margin</span>
                <span style={styles.totalVal}>{form.admin_margin || 0}%</span>
              </div>
              <div style={styles.divider} />
              <div style={{ ...styles.totalRow, marginTop: "4px" }}>
                <span style={{ ...styles.totalLabel, color: "#0f172a", fontWeight: 700 }}>Combined Total</span>
                <span style={{ ...styles.totalVal, color: "#10b981", fontSize: "18px" }}>{totalMargin.toFixed(1)}%</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={styles.actions}>
            <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button
              style={{
                ...styles.submitBtn,
                opacity: loading || (!form.agent_margin && !form.admin_margin) ? 0.6 : 1,
              }}
              onClick={handleSubmit}
              disabled={loading || (!form.agent_margin && !form.admin_margin)}
            >
              {loading ? "Saving..." : isEdit ? "Update Ratio" : "Add Ratio"}
            </button>
          </div>
        </div>
      </div>

      {/* ✅ History Modal - rendered outside main popup */}
      <MarginHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        propertyId={propertyId}
        filterActions={["add_margin_ratio", "update_margin_ratio"]}
      />
    </>
  );
};

const styles = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 10000,
  },
  modal: {
    background: "#ffffff",
    borderRadius: "24px", padding: "32px",
    width: "100%", maxWidth: "640px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 60px rgba(0,0,0,0.12)",
  },
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: "28px",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  iconBox: {
    fontSize: "28px", background: "rgba(44,71,145,0.08)",
    border: "1px solid rgba(44,71,145,0.2)",
    borderRadius: "14px", padding: "10px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  title: { color: "#0f172a", fontSize: "20px", fontWeight: 700, margin: 0 },
  subtitle: { color: "#64748b", fontSize: "13px", margin: "4px 0 0 0" },
  closeBtn: {
    background: "#f1f5f9", border: "1px solid #e2e8f0",
    color: "#64748b", borderRadius: "50%", width: "36px", height: "36px",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer",
  },
  fieldsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" },
  fieldCard: {
    background: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: "16px", padding: "18px", width: "100%"
  },
  fieldHeader: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" },
  fieldEmoji: { fontSize: "24px" },
  fieldTitle: { color: "#0f172a", fontSize: "14px", fontWeight: 600, margin: 0 },
  inputWrapper: {
    display: "flex", alignItems: "center",
    background: "#ffffff", border: "1px solid #e2e8f0",
    borderRadius: "10px", overflow: "hidden",
  },
  input: {
    flex: 1, padding: "12px 14px", background: "transparent",
    border: "none", outline: "none", color: "#0f172a",
    fontSize: "15px", fontFamily: "inherit",
  },
  inputSuffix: {
    padding: "12px 14px", color: "#2c4791",
    fontWeight: 700, fontSize: "15px",
    borderLeft: "1px solid #e2e8f0",
  },
  fieldPreview: { marginTop: "8px", fontSize: "12px", fontWeight: 600 },
  totalPreview: {
    background: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: "16px", padding: "20px", marginBottom: "24px",
  },
  totalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" },
  totalLabel: { color: "#64748b", fontSize: "14px", fontWeight: 600 },
  totalVal: { color: "#0f172a", fontSize: "15px", fontWeight: 700 },
  divider: { height: "1px", background: "#e2e8f0", margin: "6px 0" },
  actions: { display: "flex", gap: "12px" },
  cancelBtn: {
    flex: 1, padding: "14px", background: "#f8fafc",
    border: "1px solid #e2e8f0", borderRadius: "12px",
    color: "#64748b", fontSize: "15px", fontWeight: 600, cursor: "pointer",
  },
  submitBtn: {
    flex: 2, padding: "14px",
    background: "linear-gradient(135deg, #2c4791, #1a2f6e)",
    border: "none", borderRadius: "12px", color: "white",
    fontSize: "15px", fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 16px rgba(44,71,145,0.25)",
  },
};

export default MarginRatioPopup;